import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { AppLoggerService } from '@services/app-logger.service';
import { environment } from '../../../environments/environment';

const PUBLIC_ENDPOINTS = ['/oauth2/', '/public/', '/health', '/assets/'];

function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

function isOAuth2Endpoint(url: string): boolean {
  return url.includes('/oauth2/') || url.includes('/userinfo');
}

// Las peticiones al UX Gateway FNA (getFormDefinition, executeFormAction, session/*) NO se
// autentican con el access token OAuth2/Hydra: usan firma HMAC + sessionId de ecosistema. Por eso
// un 401 de esas llamadas NO significa "access token expirado" sino "sesión de ecosistema cerrada/
// expirada" (marcador SESSION_CLOSED), que ya maneja de forma amable APIGatewayService.callSrv →
// detectSessionClosed. Si dejáramos que este interceptor tratara ese 401 como expiración del token,
// dispararía un refresh OAuth espurio y, al fallar, un logout con redirección dura a Hydra que
// preempta el modal de "sesión cerrada". Por eso el manejo de 401 se salta para estas rutas.
function isFnaGatewayEndpoint(url: string): boolean {
  const gatewayRootPath = environment.appConfig?.apiRootPath;
  return !!gatewayRootPath && url.includes(gatewayRootPath);
}

let _authService: AuthService | null = null;

function getAuthService(injector: Injector): AuthService {
  if (!_authService) {
    _authService = injector.get(AuthService);
  }
  return _authService;
}

let _logger: AppLoggerService | null = null;

function getLogger(injector: Injector): AppLoggerService {
  if (!_logger) {
    _logger = injector.get(AppLoggerService);
  }
  return _logger;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (isOAuth2Endpoint(req.url)) {
    return next(req);
  }

  if (isPublicEndpoint(req.url)) {
    return next(req);
  }

  const injector = inject(Injector);
  const authService = getAuthService(injector);
  const logger = getLogger(injector);
  const token = authService.accessToken();

  let authReq = req;
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Un 401 del gateway FNA es "sesión de ecosistema cerrada", no token OAuth expirado: se deja
      // pasar para que callSrv/detectSessionClosed dispare el cierre de sesión amable. Ver
      // isFnaGatewayEndpoint.
      if (error.status === 401 && !isFnaGatewayEndpoint(req.url)) {
        return handleUnauthorizedError(authService, req, next, logger);
      }
      return throwError(() => error);
    })
  );
};

function handleUnauthorizedError(
  authService: AuthService,
  originalReq: HttpRequest<unknown>,
  next: HttpHandlerFn,
  logger: AppLoggerService
) {
  const refreshToken = authService.refreshToken();

  if (refreshToken) {
    logger.warn('[AuthInterceptor] Token expired, attempting refresh...');

    return authService.refreshAccessToken().pipe(
      switchMap(success => {
        if (success) {
          const newToken = authService.accessToken();
          const retryReq = originalReq.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          return next(retryReq);
        } else {
          logger.warn('[AuthInterceptor] Token refresh failed, logging out...');
          authService.logout();
          return throwError(() => new Error('Session expired'));
        }
      }),
      catchError(err => {
        logger.error('[AuthInterceptor] Error during token refresh:', err);
        authService.logout();
        return throwError(() => err);
      })
    );
  } else {
    logger.warn('[AuthInterceptor] No refresh token, logging out...');
    authService.logout();
    return throwError(() => new Error('Session expired'));
  }
}

export const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
  const hydraUrl = environment.oauth2.hydraPublicUrl;

  if (req.url.startsWith(hydraUrl)) {
    const credReq = req.clone({
      withCredentials: true,
    });
    return next(credReq);
  }

  return next(req);
};
